import { Trans, useTranslation } from "react-i18next";
import type { ActionKey } from "../components/toast/ToastProvider";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { PermissionGate } from "../auth/access";
import { describeError, useToast } from "../components/toast";
import { useEffect, useState, type ReactNode } from "react";
import { foyerApi } from "../http/foyer";
import CommitDrawer from "../components/CommitDrawer";
import {
  type FoyerSettings,
  DEFAULT_ALERT_OFFLINE_MINUTES,
  DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS,
  DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS,
  DEFAULT_PHOTO_RETENTION_MONTHS,
  DEFAULT_METERED_PASS_THRESHOLD_PERCENT,
  DEFAULT_METERED_FLEET_GOAL_PERCENT,
  DEFAULT_TV_STREAMING_SLUG,
  DEFAULT_CHANNEL_LOGO_OFFSET_X,
  DEFAULT_CHANNEL_LOGO_OFFSET_Y,
  DEFAULT_CHANNEL_LOGO_WIDTH,
} from "@shared/types/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { cornerEdges, ruleFor } from "@shared/tv-corners";
import HelpBadge from "../components/HelpBadge";
import { useFormat, type Formatters } from "../i18n/format";
import { errorText } from "../utils/apiError";

/**
 * This card DESCRIBES a corner it does not own (`CONTENT-26`).
 *
 * The corner belongs to `@shared/tv-corners`, so both labels and the help text
 * are derived from it rather than typed here. That is the whole point: when the
 * logo moved bottom-right -> bottom-left on 2026-09-08 (`d1fbd94`), the corner
 * had to be hand-chased across four files including this one, and a stale
 * "Margin from right" would have had an operator adjusting the wrong edge with
 * nothing to catch it.
 */
const LOGO_CORNER = ruleFor("channel-logo").corner;
const LOGO_EDGES = cornerEdges(LOGO_CORNER);
const HOSPITALITY_PLACEHOLDER = "ops@nenda.com\noncall@nenda.com";
const CARE_PLACEHOLDER = "care-ops@nenda.com";

const percent = (fmt: Formatters, value: number) =>
  fmt.number(value / 100, { style: "percent", maximumFractionDigits: 2 });

/** Split a free-text recipients box (newline/comma separated) into emails. */
function parseRecipients(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

type DurationUnit = "minutes" | "hours" | "days";
const UNIT_MINUTES: Record<DurationUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
};

/** Present a minutes value in the largest whole unit (90 → 90 min, 120 → 2 h). */
function splitMinutes(min: number): { value: number; unit: DurationUnit } {
  if (min >= 1440 && min % 1440 === 0)
    return { value: min / 1440, unit: "days" };
  if (min >= 60 && min % 60 === 0) return { value: min / 60, unit: "hours" };
  return { value: min, unit: "minutes" };
}

/** A number from a text field: NaN for blank or junk, never silently 0. */
function num(raw: string): number {
  return raw.trim() === "" ? NaN : Number(raw);
}

const atLeast = (raw: string, min: number) => num(raw) >= min;
const between = (raw: string, min: number, max: number) =>
  num(raw) >= min && num(raw) <= max;

type SectionKey =
  | "streaming"
  | "health"
  | "recipients"
  | "metered"
  | "retention"
  | "logo";

/**
 * System settings (platform admins).
 *
 * Nothing here waits for a page-level Save. Each card shows what is stored and
 * has an Edit button that opens a drawer with that card's fields; the drawer's
 * Save writes only those fields (the settings route takes a partial update) and
 * toasts. The one on/off switch - health-alert emails - saves the moment it is
 * flipped.
 */
export default function GeneralSettingsPage() {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const [settings, setSettings] = useState<FoyerSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SectionKey | null>(null);
  const [switchBusy, setSwitchBusy] = useState(false);

  useEffect(() => {
    foyerApi
      .getSettings()
      .then(setSettings)
      .catch((e) => setLoadError(errorText(e, t)));
  }, [t]);

  /** Write a partial update; true = stored (a failure has been toasted). */
  async function save(
    patch: Partial<FoyerSettings>,
    success: string,
    failure: ActionKey
  ): Promise<boolean> {
    try {
      setSettings(await foyerApi.updateSettings(patch));
      toast.success(success);
      return true;
    } catch (e) {
      toast.error(describeError(t, failure, e));
      return false;
    }
  }

  async function toggleAlerts(on: boolean) {
    setSwitchBusy(true);
    await save(
      { alertsEnabled: on },
      on
        ? t("generalSettingsPage.healthAlertEmailsOn")
        : t("generalSettingsPage.healthAlertEmailsOff"),
      on ? "turnOnHealthAlertEmails" : "turnOffHealthAlertEmails"
    );
    setSwitchBusy(false);
  }

  const s = settings;
  const threshold = s ? splitMinutes(s.alertOfflineMinutes) : null;
  const close = () => setEditing(null);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("generalSettingsPage.eyebrow")}
        title={t("generalSettingsPage.title")}
      />

      {loadError && (
        <Typography color="error" variant="body2">
          {t("generalSettingsPage.loadFailed", { reason: loadError })}
        </Typography>
      )}

      <Stack spacing={3}>
        <SettingsCard
          title={t("generalSettingsPage.streaming.title")}
          testId="streaming"
          disabled={!s}
          onEdit={() => setEditing("streaming")}
          help={t("generalSettingsPage.streaming.help")}
        >
          {/*
            Android only, and it has to say so (TIZEN-13). A Samsung panel has
            no shell to ask, so `streamingAppVersion` is null there and the
            Fleet view reports nothing - which read as "the vendor app is
            missing" rather than "this platform cannot answer". The version
            half of this promise is the part that does not travel; the slug
            itself still reaches every platform through the app catalogue.
          */}
          <Typography variant="body2">
            <Trans
              t={t}
              i18nKey="generalSettingsPage.streaming.packageSlug"
              values={{
                slug: s?.tvStreamingAppSlug || DEFAULT_TV_STREAMING_SLUG,
              }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </SettingsCard>

        <SettingsCard
          title={t("generalSettingsPage.health.title")}
          testId="health"
          disabled={!s}
          onEdit={() => setEditing("health")}
          help={t("generalSettingsPage.health.help")}
        >
          {s && threshold && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="generalSettingsPage.health.onlineWindow"
                  values={{ seconds: fmt.number(s.healthOnlineWindowSeconds) }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="generalSettingsPage.health.offlineThreshold"
                  values={{
                    duration: t(
                      `generalSettingsPage.duration.${threshold.unit}`,
                      {
                        count: threshold.value,
                      }
                    ),
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="generalSettingsPage.health.failedCommandWindow"
                  values={{
                    hours: fmt.number(s.healthFailedCommandWindowHours),
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
            </Stack>
          )}
        </SettingsCard>

        <SettingsCard
          title={t("generalSettingsPage.recipients.title")}
          testId="recipients"
          disabled={!s}
          editLabel={t("generalSettingsPage.recipients.edit")}
          onEdit={() => setEditing("recipients")}
          help={t("generalSettingsPage.recipients.help")}
        >
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <PermissionGate
                  resource="global-settings"
                  level="administrate"
                  place="nenda"
                  deniedProps={{ disabled: true }}
                >
                  <Switch
                    checked={s?.alertsEnabled ?? false}
                    onChange={(e) => void toggleAlerts(e.target.checked)}
                    disabled={!s || switchBusy}
                    data-testid="settings-alerts-enabled"
                  />
                </PermissionGate>
              }
              label={t("generalSettingsPage.recipients.sendEmails")}
            />
            <Typography variant="body2" color="text.secondary">
              <Trans
                t={t}
                i18nKey="generalSettingsPage.recipients.firesAt"
                components={{ strong: <strong /> }}
              />
            </Typography>
            <Typography variant="body2">
              <Trans
                t={t}
                i18nKey="generalSettingsPage.recipients.hospitalityList"
                values={{
                  list: s?.alertRecipientsHospitality.length
                    ? s.alertRecipientsHospitality.join(", ")
                    : t("generalSettingsPage.recipients.none"),
                }}
                components={{ strong: <strong /> }}
              />
            </Typography>
            <Typography variant="body2">
              <Trans
                t={t}
                i18nKey="generalSettingsPage.recipients.careList"
                values={{
                  list: s?.alertRecipientsCare.length
                    ? s.alertRecipientsCare.join(", ")
                    : t("generalSettingsPage.recipients.none"),
                }}
                components={{ strong: <strong /> }}
              />
            </Typography>
          </Stack>
        </SettingsCard>

        <SettingsCard
          title={t("generalSettingsPage.metered.title")}
          testId="metered"
          disabled={!s}
          onEdit={() => setEditing("metered")}
          help={t("generalSettingsPage.metered.help")}
        >
          {s && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="generalSettingsPage.metered.passLine"
                  values={{
                    percent: percent(fmt, s.meteredPassThresholdPercent),
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="generalSettingsPage.metered.fleetGoal"
                  values={{ percent: percent(fmt, s.meteredFleetGoalPercent) }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
            </Stack>
          )}
        </SettingsCard>

        <SettingsCard
          title={t("generalSettingsPage.retention.title")}
          testId="retention"
          disabled={!s}
          onEdit={() => setEditing("retention")}
          help={t("generalSettingsPage.retention.help")}
        >
          <Typography variant="body2">
            <Trans
              t={t}
              i18nKey="generalSettingsPage.retention.keptFor"
              values={{
                months: t("generalSettingsPage.monthCount", {
                  count: s?.photoRetentionMonths ?? 0,
                }),
              }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </SettingsCard>

        <SettingsCard
          title={t("generalSettingsPage.logo.title")}
          testId="logo"
          disabled={!s}
          onEdit={() => setEditing("logo")}
          help={t("generalSettingsPage.logo.help", {
            corner: t(`generalSettingsPage.corners.${LOGO_CORNER}`),
          })}
        >
          {s && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="generalSettingsPage.logo.summary"
                  values={{
                    edgeX: t(`generalSettingsPage.edges.${LOGO_EDGES.x}`),
                    edgeY: t(`generalSettingsPage.edges.${LOGO_EDGES.y}`),
                    x: fmt.number(s.channelLogoOffsetX),
                    y: fmt.number(s.channelLogoOffsetY),
                    width: fmt.number(s.channelLogoWidth),
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("generalSettingsPage.logo.frameNote")}
              </Typography>
            </Stack>
          )}
        </SettingsCard>
      </Stack>

      {s && editing === "streaming" && (
        <StreamingDrawer settings={s} save={save} onClose={close} />
      )}
      {s && editing === "health" && (
        <HealthDrawer settings={s} save={save} onClose={close} />
      )}
      {s && editing === "recipients" && (
        <RecipientsDrawer settings={s} save={save} onClose={close} />
      )}
      {s && editing === "metered" && (
        <MeteredDrawer settings={s} save={save} onClose={close} />
      )}
      {s && editing === "retention" && (
        <RetentionDrawer settings={s} save={save} onClose={close} />
      )}
      {s && editing === "logo" && (
        <LogoDrawer settings={s} save={save} onClose={close} />
      )}
    </PageContainer>
  );
}

/** One settings card: title, what is stored, and an Edit button. */
function SettingsCard({
  title,
  help,
  testId,
  disabled,
  editLabel,
  onEdit,
  children,
}: {
  title: string;
  help: string;
  testId: string;
  disabled: boolean;
  editLabel?: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation(["premises", "common"]);
  return (
    <Card sx={{ position: "relative" }} data-testid={`settings-card-${testId}`}>
      <CardContent sx={{ p: 3 }}>
        <HelpBadge title={help} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">{title}</Typography>
          <PermissionGate
            resource="global-settings"
            level="administrate"
            place="nenda"
          >
            {" "}
            <Button
              size="small"
              variant="outlined"
              onClick={onEdit}
              disabled={disabled}
              data-testid={`settings-edit-${testId}`}
              sx={{ mr: 4 }}
            >
              {editLabel ?? t("common:buttons.edit")}
            </Button>
          </PermissionGate>
        </Stack>
        <div data-testid={`settings-summary-${testId}`}>{children}</div>
      </CardContent>
    </Card>
  );
}

type SaveFn = (
  patch: Partial<FoyerSettings>,
  success: string,
  failure: ActionKey
) => Promise<boolean>;

interface DrawerProps {
  settings: FoyerSettings;
  save: SaveFn;
  onClose: () => void;
}

function StreamingDrawer({ settings, save, onClose }: DrawerProps) {
  const { t } = useTranslation("premises");
  const [slug, setSlug] = useState(settings.tvStreamingAppSlug);
  return (
    <CommitDrawer
      title={t("generalSettingsPage.streamingDrawer.title")}
      onClose={onClose}
      data-testid="settings-drawer-streaming"
      onSave={() =>
        save(
          { tvStreamingAppSlug: slug },
          t("generalSettingsPage.streamingDrawer.saved", {
            slug: slug.trim() || DEFAULT_TV_STREAMING_SLUG,
          }),
          "saveTvStreamingApp"
        )
      }
    >
      <TextField
        label={t("generalSettingsPage.streamingDrawer.slugLabel")}
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        placeholder={DEFAULT_TV_STREAMING_SLUG}
        helperText={t("generalSettingsPage.streamingDrawer.slugHelp", {
          slug: DEFAULT_TV_STREAMING_SLUG,
        })}
        fullWidth
        autoFocus
        inputProps={{
          "data-testid": "settings-streaming-slug",
          spellCheck: false,
        }}
      />
    </CommitDrawer>
  );
}

function HealthDrawer({ settings, save, onClose }: DrawerProps) {
  const { t } = useTranslation("premises");
  const initial = splitMinutes(settings.alertOfflineMinutes);
  const [onlineWindowSeconds, setOnlineWindowSeconds] = useState(
    String(settings.healthOnlineWindowSeconds)
  );
  const [thresholdValue, setThresholdValue] = useState(String(initial.value));
  const [thresholdUnit, setThresholdUnit] = useState<DurationUnit>(
    initial.unit
  );
  const [failedCommandHours, setFailedCommandHours] = useState(
    String(settings.healthFailedCommandWindowHours)
  );
  const valid =
    atLeast(onlineWindowSeconds, 10) &&
    atLeast(thresholdValue, 1) &&
    between(failedCommandHours, 1, 168);
  return (
    <CommitDrawer
      title={t("generalSettingsPage.healthDrawer.title")}
      onClose={onClose}
      saveDisabled={!valid}
      data-testid="settings-drawer-health"
      onSave={() =>
        save(
          {
            healthOnlineWindowSeconds: num(onlineWindowSeconds),
            alertOfflineMinutes:
              num(thresholdValue) * UNIT_MINUTES[thresholdUnit] ||
              DEFAULT_ALERT_OFFLINE_MINUTES,
            healthFailedCommandWindowHours: num(failedCommandHours),
          },
          t("generalSettingsPage.healthDrawer.saved"),
          "saveFleetHealthThresholds"
        )
      }
    >
      <Stack spacing={2.5}>
        <TextField
          label={t("generalSettingsPage.healthDrawer.onlineWindowLabel")}
          type="number"
          value={onlineWindowSeconds}
          onChange={(e) => setOnlineWindowSeconds(e.target.value)}
          error={!atLeast(onlineWindowSeconds, 10)}
          helperText={t("generalSettingsPage.healthDrawer.onlineWindowHelp", {
            seconds: DEFAULT_HEALTH_ONLINE_WINDOW_SECONDS,
          })}
          inputProps={{ min: 10, "data-testid": "settings-online-window" }}
        />
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <TextField
            label={t("generalSettingsPage.healthDrawer.offlineThresholdLabel")}
            type="number"
            value={thresholdValue}
            onChange={(e) => setThresholdValue(e.target.value)}
            error={!atLeast(thresholdValue, 1)}
            helperText={t(
              "generalSettingsPage.healthDrawer.offlineThresholdHelp"
            )}
            sx={{ flex: 1 }}
            inputProps={{ min: 1, "data-testid": "settings-alert-value" }}
          />
          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel>
              {t("generalSettingsPage.healthDrawer.unit")}
            </InputLabel>
            <Select
              label={t("generalSettingsPage.healthDrawer.unit")}
              value={thresholdUnit}
              onChange={(e) => setThresholdUnit(e.target.value as DurationUnit)}
              data-testid="settings-alert-unit"
            >
              <MenuItem value="minutes">
                {t("generalSettingsPage.healthDrawer.minutes")}
              </MenuItem>
              <MenuItem value="hours">
                {t("generalSettingsPage.healthDrawer.hours")}
              </MenuItem>
              <MenuItem value="days">
                {t("generalSettingsPage.healthDrawer.days")}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <TextField
          label={t("generalSettingsPage.healthDrawer.failedCommandLabel")}
          type="number"
          value={failedCommandHours}
          onChange={(e) => setFailedCommandHours(e.target.value)}
          error={!between(failedCommandHours, 1, 168)}
          helperText={t("generalSettingsPage.healthDrawer.failedCommandHelp", {
            hours: DEFAULT_HEALTH_FAILED_COMMAND_WINDOW_HOURS,
          })}
          inputProps={{
            min: 1,
            max: 168,
            "data-testid": "settings-failed-command-window",
          }}
        />
      </Stack>
    </CommitDrawer>
  );
}

function RecipientsDrawer({ settings, save, onClose }: DrawerProps) {
  const { t } = useTranslation("premises");
  const [hotel, setHotel] = useState(
    settings.alertRecipientsHospitality.join("\n")
  );
  const [care, setCare] = useState(settings.alertRecipientsCare.join("\n"));
  return (
    <CommitDrawer
      title={t("generalSettingsPage.recipientsDrawer.title")}
      onClose={onClose}
      data-testid="settings-drawer-recipients"
      onSave={() =>
        save(
          {
            alertRecipientsHospitality: parseRecipients(hotel),
            alertRecipientsCare: parseRecipients(care),
          },
          t("generalSettingsPage.recipientsDrawer.saved"),
          "saveHealthAlertRecipients"
        )
      }
    >
      <Stack spacing={2.5}>
        <TextField
          label={t("generalSettingsPage.recipients.hospitality")}
          value={hotel}
          onChange={(e) => setHotel(e.target.value)}
          placeholder={HOSPITALITY_PLACEHOLDER}
          helperText={t("generalSettingsPage.recipientsDrawer.hospitalityHelp")}
          fullWidth
          multiline
          minRows={2}
          inputProps={{
            "data-testid": "settings-recipients-hospitality",
            spellCheck: false,
          }}
        />
        <TextField
          label={t("generalSettingsPage.recipients.care")}
          value={care}
          onChange={(e) => setCare(e.target.value)}
          placeholder={CARE_PLACEHOLDER}
          helperText={t("generalSettingsPage.recipientsDrawer.careHelp")}
          fullWidth
          multiline
          minRows={2}
          inputProps={{
            "data-testid": "settings-recipients-care",
            spellCheck: false,
          }}
        />
      </Stack>
    </CommitDrawer>
  );
}

function MeteredDrawer({ settings, save, onClose }: DrawerProps) {
  const { t } = useTranslation("premises");
  const fmt = useFormat();
  const [pass, setPass] = useState(
    String(settings.meteredPassThresholdPercent)
  );
  const [goal, setGoal] = useState(String(settings.meteredFleetGoalPercent));
  const valid = between(pass, 1, 100) && between(goal, 1, 100);
  return (
    <CommitDrawer
      title={t("generalSettingsPage.meteredDrawer.title")}
      onClose={onClose}
      saveDisabled={!valid}
      data-testid="settings-drawer-metered"
      onSave={() =>
        save(
          {
            meteredPassThresholdPercent: num(pass),
            meteredFleetGoalPercent: num(goal),
          },
          t("generalSettingsPage.meteredDrawer.saved", {
            pass: percent(fmt, num(pass)),
            goal: percent(fmt, num(goal)),
          }),
          "saveMeteredUsageKpi"
        )
      }
    >
      <Stack spacing={2.5}>
        <TextField
          label={t("generalSettingsPage.meteredDrawer.passLabel")}
          type="number"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          error={!between(pass, 1, 100)}
          helperText={t("generalSettingsPage.meteredDrawer.passHelp", {
            percent: percent(fmt, DEFAULT_METERED_PASS_THRESHOLD_PERCENT),
          })}
          inputProps={{
            min: 1,
            max: 100,
            "data-testid": "settings-metered-pass-threshold",
          }}
        />
        <TextField
          label={t("generalSettingsPage.meteredDrawer.goalLabel")}
          type="number"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          error={!between(goal, 1, 100)}
          helperText={t("generalSettingsPage.meteredDrawer.goalHelp", {
            percent: percent(fmt, DEFAULT_METERED_FLEET_GOAL_PERCENT),
          })}
          inputProps={{
            min: 1,
            max: 100,
            "data-testid": "settings-metered-fleet-goal",
          }}
        />
      </Stack>
    </CommitDrawer>
  );
}

function RetentionDrawer({ settings, save, onClose }: DrawerProps) {
  const { t } = useTranslation("premises");
  const [months, setMonths] = useState(String(settings.photoRetentionMonths));
  return (
    <CommitDrawer
      title={t("generalSettingsPage.retentionDrawer.title")}
      onClose={onClose}
      saveDisabled={!atLeast(months, 1)}
      data-testid="settings-drawer-retention"
      onSave={() =>
        save(
          { photoRetentionMonths: num(months) },
          t("generalSettingsPage.retentionDrawer.saved", {
            months: t("generalSettingsPage.monthCount", { count: num(months) }),
          }),
          "saveCarePhotoRetention"
        )
      }
    >
      <TextField
        label={t("generalSettingsPage.retentionDrawer.monthsLabel")}
        type="number"
        value={months}
        onChange={(e) => setMonths(e.target.value)}
        error={!atLeast(months, 1)}
        helperText={t("generalSettingsPage.retentionDrawer.monthsHelp", {
          months: t("generalSettingsPage.monthCount", {
            count: DEFAULT_PHOTO_RETENTION_MONTHS,
          }),
        })}
        sx={{ width: 220 }}
        inputProps={{
          min: 1,
          "data-testid": "settings-photo-retention-months",
        }}
      />
    </CommitDrawer>
  );
}

function LogoDrawer({ settings, save, onClose }: DrawerProps) {
  const { t } = useTranslation("premises");
  const [x, setX] = useState(String(settings.channelLogoOffsetX));
  const [y, setY] = useState(String(settings.channelLogoOffsetY));
  const [width, setWidth] = useState(String(settings.channelLogoWidth));
  // 0 is a real, valid offset here (flush against the edge), so unlike the
  // other numbers it must reach the server as 0, never as "blank -> default".
  const valid = atLeast(x, 0) && atLeast(y, 0) && atLeast(width, 40);
  return (
    <CommitDrawer
      title={t("generalSettingsPage.logoDrawer.title")}
      onClose={onClose}
      saveDisabled={!valid}
      data-testid="settings-drawer-logo"
      onSave={() =>
        save(
          {
            channelLogoOffsetX: num(x),
            channelLogoOffsetY: num(y),
            channelLogoWidth: num(width),
          },
          t("generalSettingsPage.logoDrawer.saved"),
          "saveChannelLogoPlacement"
        )
      }
    >
      <Stack spacing={2.5}>
        <TextField
          label={t("generalSettingsPage.logoDrawer.marginFrom", {
            edge: t(`generalSettingsPage.edges.${LOGO_EDGES.x}`),
          })}
          type="number"
          value={x}
          onChange={(e) => setX(e.target.value)}
          error={!atLeast(x, 0)}
          helperText={t("generalSettingsPage.logoDrawer.defaultPx", {
            px: DEFAULT_CHANNEL_LOGO_OFFSET_X,
          })}
          inputProps={{ min: 0, "data-testid": "settings-logo-offset-x" }}
        />
        <TextField
          label={t("generalSettingsPage.logoDrawer.marginFrom", {
            edge: t(`generalSettingsPage.edges.${LOGO_EDGES.y}`),
          })}
          type="number"
          value={y}
          onChange={(e) => setY(e.target.value)}
          error={!atLeast(y, 0)}
          helperText={t("generalSettingsPage.logoDrawer.defaultPx", {
            px: DEFAULT_CHANNEL_LOGO_OFFSET_Y,
          })}
          inputProps={{ min: 0, "data-testid": "settings-logo-offset-y" }}
        />
        <TextField
          label={t("generalSettingsPage.logoDrawer.widthLabel")}
          type="number"
          value={width}
          onChange={(e) => setWidth(e.target.value)}
          error={!atLeast(width, 40)}
          helperText={t("generalSettingsPage.logoDrawer.widthHelp", {
            px: DEFAULT_CHANNEL_LOGO_WIDTH,
          })}
          inputProps={{ min: 40, "data-testid": "settings-logo-width" }}
        />
        <Typography variant="caption" color="text.secondary">
          {t("generalSettingsPage.logo.frameNote")}
        </Typography>
      </Stack>
    </CommitDrawer>
  );
}
